import React, { RefObject, useEffect } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import type { UpdateAnswers } from '../../types';
import styles from './question-card.module.scss';
import { WizardButtons } from '@components/wizard-buttons/wizard-buttons';
import { FormLegend } from '@components/offer-form/inputs/form-legend';
import { OfferFormModal } from '@components/offer-form/offer-form-modal';
import { RadioButton } from '@components/radio-button/radio-button';
import { ErrorWrapper } from '@components/error/error-wrapper';
import { PLEASE_SELECT_ERROR_MESSAGE } from '@components/offer-form/form-steps/form-variables';
import { ErrorText } from '@components/error/error-text';
import { InnerContainer } from '@components/container/inner-container';
import { WizardState } from '@hooks/utils/wizard-types';

export type HouseFormFields = {
  roofAge: string;
  roofMaterial: string;
};

export type MultiQuestionCardProps = {
  activeQuestion: PvWizardMultiQuestionType;
  onAnswerUpdated: UpdateAnswers;
  nextStep: () => void;
  previousStep: () => void;
  showBackButton?: boolean;
  wizardState: WizardState;
  sectionKey: String;
  formModals?: {
    roofAge: PvWizardContent['disclaimerText'];
    roofMaterial: PvWizardContent['disclaimerText'];
  };
  modalRef?: RefObject<HTMLDivElement>;
};

export const MultiQuestionHouseCard: React.FC<MultiQuestionCardProps> = ({
  activeQuestion,
  onAnswerUpdated,
  nextStep,
  previousStep,
  showBackButton = false,
  wizardState,
  sectionKey,
  formModals,
  modalRef
}) => {
  const {
    control,
    register,
    formState: { errors }
  } = useForm<Record<RoofDetailQuestionKey, string>>({
    defaultValues: {
      roofAge: wizardState.answers.roofDetails?.roofAge ?? '',
      roofMaterial: wizardState.answers.roofDetails?.roofMaterial ?? ''
    }
  });

  const selectedRoofAge = useWatch<HouseFormFields, 'roofAge'>({
    control,
    name: 'roofAge'
  });

  const selectedRoofMaterial = useWatch<HouseFormFields, 'roofMaterial'>({
    control,
    name: 'roofMaterial'
  });

  useEffect(() => {
    onAnswerUpdated({
      roofDetails: { roofAge: selectedRoofAge, roofMaterial: selectedRoofMaterial }
    });
  }, [selectedRoofAge, selectedRoofMaterial]);

  return (
    <div className={styles.wrapper} data-testid={`slider-${sectionKey}`}>
      <div
        className={styles.card}
        key={activeQuestion.key}
        data-testid={`${sectionKey}-${wizardState.activeSlide + 1}`}
      >
        <InnerContainer className={styles.multiQuestionHouseCard}>
          <ErrorWrapper>
            <FormLegend>
              Wie alt ist das Dach?
              {formModals && modalRef && (
                <OfferFormModal modalContent={formModals.roofAge} modalRef={modalRef} />
              )}
            </FormLegend>
            <div className={styles.houseDataGrid}>
              {activeQuestion.questions.roofAge.answerOptions.map(option => (
                <RadioButton
                  checked={option.value === selectedRoofAge}
                  value={option.value}
                  label={option.title}
                  key={option.value}
                  {...register('roofAge', { required: true })}
                  tracking={{
                    click: {
                      label: 'Weiter',
                      element: 'Dachbaujahr',
                      funnelStep: 11
                    }
                  }}
                />
              ))}
            </div>
            {errors.roofAge && (
              <ErrorText
                text={PLEASE_SELECT_ERROR_MESSAGE}
                className={styles.houseDataRadioError}
              />
            )}
          </ErrorWrapper>
          <ErrorWrapper>
            <FormLegend>
              Aus welchem Material besteht das Dach?
              {formModals && modalRef && (
                <OfferFormModal modalContent={formModals.roofMaterial} modalRef={modalRef} />
              )}
            </FormLegend>
            <div className={styles.houseDataGrid}>
              {activeQuestion.questions.roofMaterial.answerOptions.map(option => (
                <RadioButton
                  checked={option.value === selectedRoofMaterial}
                  value={option.value}
                  label={option.title}
                  key={option.value}
                  {...register('roofMaterial', { required: true })}
                  tracking={{
                    click: {
                      label: 'Weiter',
                      element: 'Dachmaterial',
                      funnelStep: 12
                    }
                  }}
                />
              ))}
            </div>
            {errors.roofMaterial && (
              <ErrorText
                text={PLEASE_SELECT_ERROR_MESSAGE}
                className={styles.houseDataRadioError}
              />
            )}
          </ErrorWrapper>
          <WizardButtons
            nextBtnClassName={styles.nextBtn}
            showBackButton={showBackButton}
            showNextButton={Boolean(selectedRoofAge && selectedRoofMaterial)}
            onClickBack={previousStep}
            onClickNext={nextStep}
          />
        </InnerContainer>
      </div>
    </div>
  );
};
